import {
	Component,
	OnInit,
	Input,
	OnDestroy,
	SimpleChanges,
	ViewEncapsulation,
	ViewChild,
	ElementRef,
	AfterContentInit
} from '@angular/core';
import { IdService } from '../../../shared/services/id.service';
import { BehaviorSubject, Subscription, of } from 'rxjs';
import { skip, map, delay } from 'rxjs/operators';
import { easeInOut } from '../animations';
import anime from 'animejs';

@Component({
	selector: 'rbc-transition-height',
	templateUrl: './transition-height.component.html',
	styleUrls: ['./transition-height.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class TransitionHeightComponent implements OnInit, OnDestroy, AfterContentInit {
	nspace = 'rbc-transition-height';
	@Input() id: string;
	@Input() duration: number = 500; // in milliseconds
	@Input() state: boolean = false; // NOTE: must be flipped b/w T/F to trigger transition
	easing = easeInOut; // ease-in-out
	trueHeight = 0;
	$animationState: BehaviorSubject<boolean>;
	$animationStateSub: Subscription;

	@ViewChild('transitionContent') transitionContent: ElementRef;

	constructor(private idService: IdService) {}

	ngOnInit() {
		this.id = this.id ? this.id : this.idService.generate();
		this.duration = typeof this.duration === 'number' ? this.duration : 500;
		this.$animationState = new BehaviorSubject<boolean>(this.state);
		this.mountAnimationStateListener();
	}

	ngAfterContentInit(): void {
		if (this.transitionContent && this.transitionContent.nativeElement) {
			this.getTrueHeight().then(height => {
				this.trueHeight = height;
				(<HTMLElement>this.transitionContent.nativeElement).classList.remove('hidden-helper');
				this.toggleTargetVisibility(<HTMLElement>this.transitionContent.nativeElement);
			});
		}
	}

	toggleTargetVisibility(target: HTMLElement) {
		if (this.$animationState.getValue()) {
			target.classList.remove('hidden');
		} else {
			target.classList.add('hidden');
		}
	}

	// absolutely positions the element, gets the height AFTER 1 frame of browser render, then hides the elemeent
	// NOTE: this has to be done because there is currently no way to get the height of an element
	// when it's hidden. The height has to be fetched with the element not in the DOM flow, then inserted back in as hidden.
	async getTrueHeight() {
		// fetch height quickly before children are hidden
		(<HTMLElement>this.transitionContent.nativeElement).classList.add('hidden-helper');
		return await of(null)
			.pipe(
				delay(1),
				map(() => (<HTMLElement>this.transitionContent.nativeElement).offsetHeight)
			)
			.toPromise();
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes) {
			if (changes.state && !changes.state.firstChange) {
				this.$animationState.next(!this.$animationState.getValue());
			}
		}
	}

	// mounts listener and triggers animation based on state
	mountAnimationStateListener() {
		this.$animationStateSub = this.$animationState.pipe(skip(1)).subscribe(state => {
			if (state) {
				// false -> true
				this.entryAnimation();
			} else {
				// true -> false
				this.exitAnimation();
			}
		});
	}

	removeAnimation(targetElement?: Node | NodeList) {
		if (targetElement) {
			anime.remove(targetElement);
		} else if (this.transitionContent && this.transitionContent.nativeElement) {
			anime.remove(this.transitionContent.nativeElement);
		}
	}

	entryAnimation() {
		this.removeAnimation(this.transitionContent.nativeElement);
		if (this.transitionContent && this.transitionContent.nativeElement) {
			anime({
				targets: this.transitionContent.nativeElement,
				height: this.trueHeight + 'px',
				easing: this.easing,
				duration: this.duration,
				round: 100,
				begin: function(anim) {
					if (anim.animatables && anim.animatables[0]) {
						(<HTMLElement>anim.animatables[0].target).classList.remove('hidden');
						(<HTMLElement>anim.animatables[0].target).classList.add('hide-overflow');
					}
				}
			});
		}
	}

	exitAnimation() {
		if (this.transitionContent && this.transitionContent.nativeElement) {
			this.removeAnimation(this.transitionContent.nativeElement);
			anime({
				targets: this.transitionContent.nativeElement,
				height: [this.trueHeight + 'px', 0],
				easing: this.easing,
				duration: this.duration,
				round: 100,
				begin: function(anim) {
					if (anim.animatables && anim.animatables[0]) {
						(<HTMLElement>anim.animatables[0].target).classList.add('hide-overflow');
					}
				},
				complete: function(anim) {
					if (anim.animatables && anim.animatables[0]) {
						(<HTMLElement>anim.animatables[0].target).classList.add('hidden');
						(<HTMLElement>anim.animatables[0].target).classList.remove('hide-overflow');
						(<HTMLElement>anim.animatables[0].target).style.height = 'auto'; // 'reset'
					}
				}
			});
		}
	}
}
